import React, { useMemo, useState } from "react";
import Lottie from "react-lottie-player";
import { BoxWithShadow } from "../../components/containers/styledComponents";
import { IconContainer } from "../../components/visuals/styledComponents";
import { RowContainer } from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import { ValidationErrorDisplay } from "../../components/common/ValidationErrorDisplay";
import { CompassWhiteButton } from "../../components/buttons/styledComponents";
import { compassDarBlue, blueText, greyText } from "../../styles";
import failAnimation from "../../assets/lottie/fail_animation.json";
import { ValidationResponse } from "../../types/validationTypes";
import {
  ValidationLayer,
  VALIDATION_LAYER_LABELS,
} from "../../enums/Validation";
import { usePost } from "../../hooks/usePost";

interface FileListResponse {
  path: string;
  files: Array<{
    key: string;
    name: string;
    size: number;
    last_modified: string;
  }>;
  count: number;
}

interface FileDownloadResponse {
  url: string;
  path: string;
  expires_in: number;
}

interface FailedUploadBoxProps {
  validationResponse: ValidationResponse;
  filename: string;
  onRetry: () => void;
}

/**
 * Component displayed when file validation fails
 * Shows fail animation, failed layer info, and error table
 */
export const FailedUploadBox: React.FC<FailedUploadBoxProps> = ({
  validationResponse,
  filename,
  onRetry,
}) => {
  const failedLayer = validationResponse.data?.failed_layer as ValidationLayer;
  const layerLabel = failedLayer ? VALIDATION_LAYER_LABELS[failedLayer] : "";
  const s3ErrorPath = validationResponse.result?.s3_error_path;
  const [isDownloading, setIsDownloading] = useState(false);

  const { sendData: listFiles } = usePost<FileListResponse, { path: string }>();
  const { sendData: getDownloadUrl } = usePost<
    FileDownloadResponse,
    { path: string }
  >();

  // Memoize the animation to prevent re-renders from restarting it
  const failAnimationElement = useMemo(
    () => <Lottie loop={false} animationData={failAnimation} play />,
    []
  );

  const handleDownloadErrors = async (): Promise<void> => {
    if (!s3ErrorPath || isDownloading) return;

    setIsDownloading(true);
    try {
      // First list files in the error directory
      const listResponse = await listFiles("FILE_LIST", {
        path: s3ErrorPath,
      });

      if (listResponse?.files && listResponse.files.length > 0) {
        // Find the errors CSV file
        const errorsCsv = listResponse.files.find((file) =>
          file.key.toLowerCase().endsWith("errors.csv")
        );

        if (errorsCsv) {
          const downloadResponse = await getDownloadUrl("FILE_DOWNLOAD", {
            path: errorsCsv.key,
          });

          if (downloadResponse?.url) {
            window.open(downloadResponse.url, "_blank");
          }
        }
      }
    } catch (error) {
      console.error("Failed to download errors:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <BoxWithShadow>
      <IconContainer size={150} m={1} p={1}>
        {failAnimationElement}
      </IconContainer>

      <RowContainer jc="center" mt={-42}>
        <CompassParagraph fontSize={17} bold={true}>
          {layerLabel ? `${layerLabel} Failed` : "Validation Failed"}
        </CompassParagraph>
      </RowContainer>

      <RowContainer jc="center">
        <CompassParagraph color={compassDarBlue} underline={true} mr={5}>
          {filename}
        </CompassParagraph>
        <CompassParagraph fontSize={13} color={greyText} mr={4}>
          contains errors that need to be fixed. Download errors report
        </CompassParagraph>
        {s3ErrorPath && (
          <CompassParagraph
            fontSize={13}
            color={blueText}
            underline={true}
            style={{ cursor: "pointer" }}
            onClick={handleDownloadErrors}
          >
            here
          </CompassParagraph>
        )}
        {!s3ErrorPath && (
          <CompassParagraph fontSize={13} color={greyText}>
            .
          </CompassParagraph>
        )}
      </RowContainer>

      {/* Validation Error Display */}
      <ValidationErrorDisplay validationResponse={validationResponse} />

      <RowContainer jc="center" mt={2}>
        <CompassWhiteButton size="s" onClick={onRetry}>
          Upload New File
        </CompassWhiteButton>
      </RowContainer>
    </BoxWithShadow>
  );
};
